import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const SaasDashboard = () => {
  const [stats, setStats] = useState(null);
  const [enterpriseMetrics, setEnterpriseMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, entRes] = await Promise.all([
        saasPlatformService.getDashboardStats().catch(err => {
          console.warn('Dashboard stats fallback', err);
          return null;
        }),
        saasPlatformService.getOverviewMetrics().catch(err => {
          console.warn('Overview metrics fallback', err);
          return null;
        })
      ]);

      if (statsRes && statsRes.data) {
        setStats(statsRes.data);
      } else {
        setStats(statsRes || {});
      }

      if (entRes && entRes.data) {
        setEnterpriseMetrics(entRes.data);
      } else {
        setEnterpriseMetrics(entRes || {});
      }
    } catch (err) {
      console.error('Failed to load SaaS dashboard stats', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to connect to Platform Catalog.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="saas-container">
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>☁️</span> LIMS SaaS Platform Control Center
          </h1>
          <p>Enterprise Diagnostics Governance, Multi-Tenant Catalog, Billing &amp; Clinical Quality Engine</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/guide')}>
            📘 Operations Playbook &amp; Flowchart
          </button>
          <button className="saas-btn saas-btn-secondary" onClick={loadStats} disabled={loading}>
            🔄 Refresh Stats
          </button>
          <button className="saas-btn saas-btn-primary" onClick={() => navigate('/saas/onboarding')}>
            ➕ Onboard Client
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div>
            <strong>Platform Alert:</strong> {error}
          </div>
          <button className="saas-btn saas-btn-secondary" style={{ padding: '0.3rem 0.6rem' }} onClick={loadStats}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="saas-loading">
          <div style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>⏳</div>
          Loading real-time enterprise platform metrics...
        </div>
      ) : (
        <>
          {/* Operations Playbook & Lifecycle Flowchart Banner */}
          <div
            className="saas-card"
            style={{
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
              border: '1px solid #bfdbfe',
              cursor: 'pointer',
              padding: '1.25rem 1.5rem',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 6px rgba(37,99,235,0.08)'
            }}
            onClick={() => navigate('/saas/guide')}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontSize: '2.25rem', background: '#fff', width: '56px', height: '56px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.06)' }}>
                  📘
                </div>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem 0', color: '#1e3a8a', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    SaaS Operations Playbook, Lifecycle Flowchart &amp; Quota Guide
                    <span className="saas-badge saas-badge-active" style={{ fontSize: '0.72rem' }}>Recommended Starting Point</span>
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.84rem', color: '#1d4ed8' }}>
                    कदम-दर-कदम संचालन फ्लोचार्ट: Step 1 (Plans) ➔ Step 2 (Onboard) ➔ Step 3 (Fleet) ➔ Step 4 (Centres) ➔ Step 5 (Wallet) ➔ Step 6 (Quotas &amp; Overdue Lock)
                  </p>
                </div>
              </div>
              <button className="saas-btn saas-btn-primary" style={{ padding: '0.55rem 1.25rem', fontWeight: 600 }}>
                Open Playbook &amp; Flowchart →
              </button>
            </div>
          </div>

          {/* Key Metric Cards */}
          <div className="saas-stats-grid" style={{ marginBottom: '1.5rem' }}>
            <div
              className="saas-stat-card saas-stat-card-clickable"
              onClick={() => navigate('/saas/tenants')}
              title="Click to view all registered tenants"
              role="button"
              tabIndex={0}
            >
              <span className="saas-stat-label">Total Registered Tenants</span>
              <span className="saas-stat-value">{enterpriseMetrics?.totalTenants ?? stats?.totalTenants ?? 0}</span>
              <span className="saas-stat-subtext">Across all isolation tiers →</span>
            </div>

            <div
              className="saas-stat-card saas-stat-card-clickable"
              onClick={() => navigate('/saas/tenants?status=ACTIVE')}
              title="Click to filter active tenants"
              role="button"
              tabIndex={0}
            >
              <span className="saas-stat-label">Active Tenants</span>
              <span className="saas-stat-value" style={{ color: '#047857' }}>
                {enterpriseMetrics?.activeTenants ?? stats?.activeTenants ?? 0}
              </span>
              <span className="saas-stat-subtext">Online &amp; routing requests →</span>
            </div>

            <div
              className="saas-stat-card saas-stat-card-clickable"
              onClick={() => navigate('/saas/lifecycle')}
              title="Click to view locked or suspended tenants"
              role="button"
              tabIndex={0}
            >
              <span className="saas-stat-label">Admin Locked / Suspended</span>
              <span className="saas-stat-value" style={{ color: (enterpriseMetrics?.adminLockedTenants || enterpriseMetrics?.suspendedTenants) ? '#b91c1c' : '#64748b' }}>
                {(enterpriseMetrics?.adminLockedTenants ?? 0) + (enterpriseMetrics?.suspendedTenants ?? 0)}
              </span>
              <span className="saas-stat-subtext">Fail-closed restrictions →</span>
            </div>

            <div
              className="saas-stat-card saas-stat-card-clickable"
              onClick={() => navigate('/saas/plans')}
              title="Click to view active subscription MRR"
              role="button"
              tabIndex={0}
            >
              <span className="saas-stat-label">Monthly Recurring Revenue</span>
              <span className="saas-stat-value" style={{ color: '#0284c7' }}>
                ₹{Number(enterpriseMetrics?.totalMrrInr ?? 0).toLocaleString()}
              </span>
              <span className="saas-stat-subtext">{enterpriseMetrics?.activeSubscriptions ?? 0} Active Subscriptions →</span>
            </div>

            <div
              className="saas-stat-card saas-stat-card-clickable"
              onClick={() => navigate('/saas/wallet')}
              title="Click to view prepaid credit balances"
              role="button"
              tabIndex={0}
            >
              <span className="saas-stat-label">Available Prepaid Credits</span>
              <span className="saas-stat-value" style={{ color: '#10b981' }}>
                {Number(enterpriseMetrics?.totalCreditsAvailable ?? 0).toLocaleString()}
              </span>
              <span className="saas-stat-subtext">{Number(enterpriseMetrics?.totalCreditsConsumed ?? 0).toLocaleString()} Consumed →</span>
            </div>

            <div
              className="saas-stat-card saas-stat-card-clickable"
              onClick={() => navigate('/saas/health?tab=incidents')}
              title="Click to view and resolve active incidents"
              role="button"
              tabIndex={0}
            >
              <span className="saas-stat-label">Active Incidents</span>
              <span className="saas-stat-value" style={{ color: (stats?.activeIncidentsCount || 0) > 0 ? '#b91c1c' : '#047857' }}>
                {stats?.activeIncidentsCount ?? 0}
              </span>
              <span className="saas-stat-subtext">Unresolved operations alerts →</span>
            </div>
          </div>

          {/* Enterprise Navigation Modules Matrix */}
          <div className="saas-card" style={{ marginBottom: '1.5rem' }}>
            <div className="saas-card-header">
              <h3>Enterprise SaaS Administration Modules</h3>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Full operational control suite for enterprise clinical diagnostic chains
              </span>
            </div>
            <div className="saas-card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
              <div
                className="saas-card"
                style={{ padding: '1.25rem', marginBottom: 0, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s ease' }}
                onClick={() => navigate('/saas/clinical')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🔬</span>
                  <strong style={{ fontSize: '0.9375rem', color: '#0284c7' }}>Clinical &amp; Reference Ranges</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b' }}>
                  Tenant-isolated investigation catalogs, draft revision diffs &amp; Pathologist sign-off.
                </p>
              </div>

              <div
                className="saas-card"
                style={{ padding: '1.25rem', marginBottom: 0, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s ease' }}
                onClick={() => navigate('/saas/plans')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>💳</span>
                  <strong style={{ fontSize: '0.9375rem', color: '#0284c7' }}>Plans &amp; Subscriptions</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b' }}>
                  Commercial tiers (Starter, Pro, Enterprise), user quotas &amp; automated renewal cycles.
                </p>
              </div>

              <div
                className="saas-card"
                style={{ padding: '1.25rem', marginBottom: 0, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s ease' }}
                onClick={() => navigate('/saas/wallet')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>💰</span>
                  <strong style={{ fontSize: '0.9375rem', color: '#0284c7' }}>Credits, Wallet &amp; Usage</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b' }}>
                  Append-only immutable transaction ledger, idempotent top-ups &amp; invoices.
                </p>
              </div>

              <div
                className="saas-card"
                style={{ padding: '1.25rem', marginBottom: 0, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s ease' }}
                onClick={() => navigate('/saas/centres')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🏥</span>
                  <strong style={{ fontSize: '0.9375rem', color: '#0284c7' }}>Centres, Doctors &amp; Staff</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b' }}>
                  Multi-branch facilities, clinical designations &amp; report verification privileges.
                </p>
              </div>

              <div
                className="saas-card"
                style={{ padding: '1.25rem', marginBottom: 0, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s ease' }}
                onClick={() => navigate('/saas/lifecycle')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🛡️</span>
                  <strong style={{ fontSize: '0.9375rem', color: '#0284c7' }}>Lifecycle &amp; Module Controls</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b' }}>
                  Instant administrative lock, granular service toggle switches &amp; safe offboarding.
                </p>
              </div>

              <div
                className="saas-card"
                style={{ padding: '1.25rem', marginBottom: 0, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.15s ease' }}
                onClick={() => navigate('/saas/health')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🩺</span>
                  <strong style={{ fontSize: '0.9375rem', color: '#0284c7' }}>Database &amp; Schema Health</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b' }}>
                  Per-tenant connection monitoring, schema drift inspection &amp; migration dry-run.
                </p>
              </div>
            </div>
          </div>

          {/* Platform Status & Quick Links */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
            <div className="saas-card" style={{ marginBottom: 0 }}>
              <div className="saas-card-header">
                <h3>Central Platform Catalog</h3>
                <span className="saas-badge saas-badge-active">
                  ● {stats?.platformCatalogStatus || 'ONLINE'}
                </span>
              </div>
              <div className="saas-card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>Catalog Database:</span>
                    <strong style={{ fontFamily: 'monospace' }}>lis_platform_catalog_test</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>Active Plans in Catalog:</span>
                    <span>{enterpriseMetrics?.planDistribution?.length ?? 3} Active Commercial Tiers</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Credential Encryption:</span>
                    <strong style={{ color: '#0284c7' }}>AES-256-GCM (AAD bound)</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="saas-card" style={{ marginBottom: 0 }}>
              <div className="saas-card-header">
                <h3>Platform Management Actions</h3>
              </div>
              <div className="saas-card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <button
                  className="saas-btn saas-btn-secondary"
                  style={{ justifyContent: 'center', padding: '1rem' }}
                  onClick={() => navigate('/saas/tenants')}
                >
                  🏢 Manage Tenants
                </button>
                <button
                  className="saas-btn saas-btn-secondary"
                  style={{ justifyContent: 'center', padding: '1rem' }}
                  onClick={() => navigate('/saas/onboarding')}
                >
                  ➕ Onboard Tenant
                </button>
                <button
                  className="saas-btn saas-btn-secondary"
                  style={{ justifyContent: 'center', padding: '1rem' }}
                  onClick={() => navigate('/saas/audit')}
                >
                  📜 Security &amp; Audit
                </button>
                <button
                  className="saas-btn saas-btn-secondary"
                  style={{ justifyContent: 'center', padding: '1rem' }}
                  onClick={() => navigate('/saas/keys')}
                >
                  🔑 Key Lifecycle
                </button>
              </div>
            </div>
          </div>

          {/* Recent Audit Events Stream */}
          <div className="saas-card">
            <div className="saas-card-header">
              <h3>Recent Platform Audit Events</h3>
              <button
                className="saas-btn saas-btn-secondary"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                onClick={() => navigate('/saas/audit')}
              >
                View Full Audit Log →
              </button>
            </div>
            <div className="saas-table-container">
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>Audit ID</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Details</th>
                    <th>IP Address</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentEvents && stats.recentEvents.length > 0 ? (
                    stats.recentEvents.map((evt) => (
                      <tr key={evt.auditID}>
                        <td style={{ fontFamily: 'monospace' }}>#{evt.auditID}</td>
                        <td>
                          <span
                            className={
                              evt.action.includes('SUSPEND')
                                ? 'saas-badge saas-badge-suspended'
                                : evt.action.includes('ONBOARD')
                                ? 'saas-badge saas-badge-active'
                                : 'saas-badge saas-badge-info'
                            }
                          >
                            {evt.action}
                          </span>
                        </td>
                        <td><strong>{evt.actorUsername}</strong></td>
                        <td style={{ color: '#475569' }}>{evt.details || '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{evt.ipAddress || '—'}</td>
                        <td style={{ color: '#64748b' }}>
                          {new Date(evt.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                        No platform audit events recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SaasDashboard;
